import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import PostCard from "@/components/PostCard";
import TesterBadge from "@/components/TesterBadge";
import BetaVendorBadge from "@/components/BetaVendorBadge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ImagePlus, Pencil, MessageSquare, MapPin, UserPlus, UserCheck, KeyRound, X } from "lucide-react";
import { CURRENCIES } from "@/lib/currency";
import BakerLocationSection from "@/components/BakerLocationSection";

export default function Profile() {
  const { username } = useParams();
  const { user, updateProfile } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [bio, setBio] = useState("");
  const [city, setCity] = useState("");
  const [stateField, setStateField] = useState("");
  const [country, setCountry] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [uploading, setUploading] = useState(false);
  const [followState, setFollowState] = useState({ is_following: false, follower_count: 0, following_count: 0 });
  const [followBusy, setFollowBusy] = useState(false);

  const loadFollow = async (uname) => {
    try { const { data } = await api.get(`/users/${uname}/follow-state`); setFollowState(data); } catch { /* ignore */ }
  };

  const toggleFollow = async () => {
    if (!user || user === false) { nav("/login"); return; }
    setFollowBusy(true);
    try {
      const { data } = followState.is_following
        ? await api.delete(`/users/${username}/follow`)
        : await api.post(`/users/${username}/follow`);
      setFollowState({ ...followState, is_following: data.is_following, follower_count: data.follower_count });
      toast.success(data.is_following ? `Following @${username}` : `Unfollowed @${username}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setFollowBusy(false);
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/users/${username}`);
      setProfile(data);
      setBio(data.bio || "");
      setCity(data.city || "");
      setStateField(data.state || "");
      setCountry(data.country || "");
      setCurrency(data.currency || "USD");
      const p = await api.get("/posts", { params: { author_id: data.id } });
      setPosts(p.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    if (username) loadFollow(username);
    // eslint-disable-next-line
  }, [username]);

  const isMe = user && user !== false && profile && user.id === profile.id;

  const saveBio = async () => {
    const res = await updateProfile({ bio, city, state: stateField, country, currency });
    if (res.ok) {
      toast.success("Profile updated");
      setEditing(false);
      load();
    } else {
      toast.error(res.error);
    }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const res = await updateProfile({ avatar_url: data.path });
      if (res.ok) {
        toast.success("Avatar updated");
        load();
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleLike = async (post) => {
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      setPosts((ps) => ps.map((p) => (p.id === post.id ? { ...p, ...data } : p)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!profile) {
    return (
      <>
        <Header />
        <div className="p-8 text-[#857163]">Loading…</div>
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="cp-card p-6 sm:p-8" data-testid="profile-header">
          <div className="flex items-start gap-5 flex-wrap">
            <div className="relative">
              <Avatar className="w-24 h-24 border-2 border-[#EBDCD1]">
                <AvatarImage src={fileUrl(profile.avatar_url)} />
                <AvatarFallback className="bg-[#FFD07F] text-[#3B2C24] font-heading text-3xl font-bold">
                  {profile.username?.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {isMe && (
                <label className="absolute -bottom-1 -right-1 bg-[#FF7E67] rounded-full p-1.5 cursor-pointer text-white shadow-md" data-testid="upload-avatar-label">
                  <input type="file" accept="image/*" onChange={uploadAvatar} className="hidden" data-testid="upload-avatar-input" />
                  <ImagePlus className="w-3.5 h-3.5" />
                </label>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#3B2C24]">@{profile.username}</h1>
                {profile.is_tester && <TesterBadge />}
                {profile.is_beta_tester && <BetaVendorBadge program={profile.beta_program} />}
              </div>
              <div className="text-sm text-[#857163] mt-1 flex flex-wrap gap-x-4 gap-y-1">
                <span data-testid="profile-post-count">{profile.post_count} posts</span>
                <span data-testid="profile-follower-count"><b className="text-[#3B2C24]">{followState.follower_count}</b> followers</span>
                <span data-testid="profile-following-count"><b className="text-[#3B2C24]">{followState.following_count}</b> following</span>
              </div>
              {editing ? (
                <div className="mt-3 space-y-3">
                  <textarea
                    className="cp-input min-h-[80px] resize-none"
                    placeholder="Tell the community about yourself…"
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    data-testid="bio-input"
                  />
                  <div className="grid sm:grid-cols-3 gap-2">
                    <label className="block">
                      <span className="text-xs text-[#857163]">City (optional)</span>
                      <input className="cp-input mt-1" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Boston" data-testid="city-input" />
                    </label>
                    <label className="block">
                      <span className="text-xs text-[#857163]">State / Region (optional)</span>
                      <input className="cp-input mt-1" value={stateField} onChange={(e) => setStateField(e.target.value)} placeholder="MA" data-testid="state-input" />
                    </label>
                    <label className="block">
                      <span className="text-xs text-[#857163]">Country (optional)</span>
                      <input className="cp-input mt-1" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="United States" data-testid="country-input" />
                    </label>
                  </div>
                  <label className="block">
                    <span className="text-xs text-[#857163]">Preferred currency (for Baker's Tools)</span>
                    <select className="cp-input mt-1" value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="currency-input">
                      {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.symbol} {c.code} — {c.name}</option>)}
                    </select>
                  </label>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setEditing(false)} className="cp-btn-ghost">Cancel</button>
                    <button onClick={saveBio} className="cp-btn-primary" data-testid="save-bio-btn">Save</button>
                  </div>
                </div>
              ) : (
                <>
                  {(profile.city || profile.state || profile.country) && (
                    <div className="mt-1 text-sm text-[#857163] inline-flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {[profile.city, profile.state, profile.country].filter(Boolean).join(", ")}
                    </div>
                  )}
                  <p className="mt-3 text-[#3B2C24]">
                    {profile.bio || <span className="text-[#857163] italic">No bio yet.</span>}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {isMe ? (
                      <>
                        <button onClick={() => setEditing(true)} className="cp-btn-ghost inline-flex items-center gap-1.5" data-testid="edit-bio-btn">
                          <Pencil className="w-3.5 h-3.5" /> Edit profile
                        </button>
                        <button onClick={() => setShowChangePw(true)} className="cp-btn-ghost inline-flex items-center gap-1.5" data-testid="change-password-btn">
                          <KeyRound className="w-3.5 h-3.5" /> Change password
                        </button>
                      </>
                    ) : user && user !== false && (
                      <>
                        <button
                          onClick={toggleFollow}
                          disabled={followBusy}
                          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition disabled:opacity-60 ${
                            followState.is_following
                              ? "bg-white border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
                              : "text-white hover:opacity-90"
                          }`}
                          style={followState.is_following ? {} : { background: "#FF7E67" }}
                          data-testid="follow-btn"
                        >
                          {followState.is_following ? <UserCheck className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                          {followState.is_following ? "Following" : "Follow"}
                        </button>
                        <button
                          onClick={() => nav(`/messages/${profile.id}`)}
                          className="cp-btn-ghost inline-flex items-center gap-1.5"
                          data-testid="message-user-btn"
                        >
                          <MessageSquare className="w-4 h-4" /> Message
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6">
          {isMe && !profile.is_seller && (
            <div className="mb-6">
              <BakerLocationSection mode="independent" username={profile.username} />
            </div>
          )}
          <h2 className="font-heading text-2xl font-semibold text-[#3B2C24] mb-4">Posts</h2>
          {uploading && <div className="text-sm text-[#857163] mb-3">Uploading avatar…</div>}
          <div className="space-y-4">
            {posts.length === 0 ? (
              <div className="cp-card p-6 text-center text-[#857163]">No posts yet.</div>
            ) : (
              posts.map((p) => <PostCard key={p.id} post={p} onLike={handleLike} />)
            )}
          </div>
        </div>
      </main>
      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
    </>
  );
}

function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 8) { setErr("New password must be at least 8 characters."); return; }
    if (next !== confirm) { setErr("New passwords don't match."); return; }
    if (next === current) { setErr("Please choose a new password that's different from your current one."); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated 🔒");
      onClose();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Couldn't change your password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="change-password-modal">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 font-heading text-lg text-[#3B2C24]">
            <KeyRound className="w-4 h-4 text-[#FF7E67]" /> Change password
          </div>
          <button onClick={onClose} className="text-[#857163] hover:text-[#3B2C24]" data-testid="change-password-close"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-[#857163] mb-3">Enter your current password, then choose a new one (at least 8 characters).</p>
        <form onSubmit={submit} className="space-y-2.5">
          <label className="block">
            <span className="text-xs text-[#857163]">Current password</span>
            <input
              type="password"
              autoComplete="current-password"
              className="cp-input mt-1"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              data-testid="change-password-current"
            />
          </label>
          <label className="block">
            <span className="text-xs text-[#857163]">New password</span>
            <input
              type="password"
              autoComplete="new-password"
              className="cp-input mt-1"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={8}
              data-testid="change-password-new"
            />
          </label>
          <label className="block">
            <span className="text-xs text-[#857163]">Confirm new password</span>
            <input
              type="password"
              autoComplete="new-password"
              className="cp-input mt-1"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              data-testid="change-password-confirm"
            />
          </label>
          {err && (
            <div className="text-xs px-3 py-2 rounded-xl bg-[#FFEEEE] border border-[#C2185B]/30 text-[#7F1D1D]" data-testid="change-password-error">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="cp-btn-primary w-full disabled:opacity-60"
            data-testid="change-password-submit"
          >
            {submitting ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}
